import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Hammer, Loader2, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login, verify2fa, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [need2fa, setNeed2fa] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (r.ok) {
      navigate("/");
    } else if (r.requires_2fa) {
      setNeed2fa(true);
      setTempToken(r.temp_token);
      setCode("");
    } else {
      setError(r.error);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await verify2fa(tempToken, code);
    setLoading(false);
    if (r.ok) navigate("/");
    else setError(r.error);
  };

  const cancel2fa = () => {
    setNeed2fa(false);
    setTempToken("");
    setCode("");
    setError("");
    setUseBackup(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]" data-testid="login-page">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-11 h-11 rounded-lg bg-[#1E3A8A] flex items-center justify-center relative">
              <span className="font-display text-white text-2xl font-extrabold leading-none">T</span>
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#E91E63] border-2 border-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-xl font-bold tracking-tight text-[#0F1A3D]">TPCO Sénégal</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#E91E63] font-bold mt-1">Touba Pro Construction</span>
            </div>
          </div>

          <div className="label-tiny mb-3">Espace privé</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-[#0F1A3D] leading-[1.05] mb-3">
            Bienvenue.
          </h1>
          <p className="text-[#4B5775] text-sm mb-10">
            Connectez-vous pour gérer vos biens, contrats et paiements.
          </p>

          <form onSubmit={need2fa ? submit2fa : submit} className="space-y-4" data-testid={need2fa ? "login-2fa-form" : "login-form"}>
            {!need2fa && (
              <>
                <div>
                  <label className="label-tiny block mb-2">Email</label>
                  <input
                    data-testid="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="vous@tpco.sn"
                    className="w-full h-11 px-3.5 rounded-md border border-[#E2E5F1] bg-white text-sm focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/15 transition"
                  />
                </div>
                <div>
                  <label className="label-tiny block mb-2">Mot de passe</label>
                  <input
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full h-11 px-3.5 rounded-md border border-[#E2E5F1] bg-white text-sm focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/15 transition"
                  />
                </div>
              </>
            )}

            {need2fa && (
              <>
                <div className="bg-[#E8EBFA] border border-[#C5CCE8] rounded-md p-3 flex items-start gap-2.5">
                  <ShieldCheck className="w-5 h-5 text-[#1E3A8A] shrink-0 mt-0.5" />
                  <div className="text-xs text-[#0F1A3D]">
                    <div className="font-bold">Vérification 2FA requise</div>
                    <div className="text-[#4B5775] mt-0.5">{useBackup ? "Saisissez un code de récupération (8 caractères)" : "Ouvrez votre application TOTP et saisissez le code à 6 chiffres"}</div>
                  </div>
                </div>
                <div>
                  <label className="label-tiny block mb-2">{useBackup ? "Code de récupération" : "Code à 6 chiffres"}</label>
                  <input
                    data-testid="login-2fa-code"
                    type="text"
                    inputMode={useBackup ? "text" : "numeric"}
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
                    required
                    maxLength={useBackup ? 10 : 6}
                    placeholder={useBackup ? "XXXXXXXX" : "123 456"}
                    className="w-full h-12 px-3.5 rounded-md border border-[#E2E5F1] bg-white text-lg font-mono tracking-widest text-center focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/15 transition"
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setUseBackup(!useBackup); setCode(""); }}
                  className="text-xs text-[#1E3A8A] hover:underline inline-flex items-center gap-1.5"
                  data-testid="login-2fa-toggle-backup"
                >
                  <KeyRound className="w-3 h-3" />
                  {useBackup ? "Utiliser le code TOTP" : "J'ai perdu mon téléphone — utiliser un code de récupération"}
                </button>
              </>
            )}

            {error && (
              <div className="text-sm text-[#C83B3B] bg-[#FBEAE9] border border-[#F4CFCE] rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              data-testid={need2fa ? "login-2fa-submit" : "login-submit"}
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] active:bg-[#172E6E] text-white font-semibold text-sm transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Vérification…" : need2fa ? "Vérifier" : "Se connecter"}
            </button>

            {need2fa && (
              <button
                type="button"
                onClick={cancel2fa}
                className="w-full text-xs text-[#4B5775] hover:text-[#0F1A3D] inline-flex items-center justify-center gap-1.5"
              >
                <ArrowLeft className="w-3 h-3" /> Retour à la connexion
              </button>
            )}
          </form>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-[#0F1A3D]">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(233,30,99,0.45) 0%, transparent 35%), radial-gradient(circle at 80% 70%, rgba(30,58,138,0.65) 0%, transparent 40%)",
          }}
        />
        <div className="absolute inset-0">
          <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
            <defs>
              <pattern id="grid" width="6" height="6" patternUnits="userSpaceOnUse">
                <path d="M 6 0 L 0 0 0 6" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.2" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <Hammer className="w-7 h-7 text-[#E91E63]" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-white/60">Promotion immobilière</span>
          </div>
          <div className="font-display text-5xl xl:text-6xl font-bold tracking-tight leading-[1.02] mb-6 max-w-md">
            On <span className="text-[#E91E63]">construit</span>,<br />
            on <span className="text-[#E91E63]">vend</span>,<br />
            on <span className="text-[#E91E63]">loue</span>.
          </div>
          <p className="text-sm text-white/70 max-w-md leading-relaxed">
            Le suivi complet de votre patrimoine TPCO — immeubles, locataires, ventes, encaissements et rappels, centralisés depuis Touba.
          </p>
          <div className="mt-10 pt-6 border-t border-white/10 text-xs uppercase tracking-[0.2em] font-semibold text-white/50">
            v1.1 · Édition Sénégal
          </div>
        </div>
      </div>
    </div>
  );
}
